import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type SubscriptionView = 'subscription' | 'invitations';

interface UseSubscriptionViewManagementOptions {
  hasPendingInvitations: boolean;
  isFetching: boolean; // Should be true if data is loading OR if pendingSeatInvitations === null
}

interface UseSubscriptionViewManagementResult {
  view: SubscriptionView;
  setView: (newView: SubscriptionView) => void;
}

/**
 * Hook to manage the subscription page view state (subscription vs invitations).
 * Handles URL param synchronization and automatic redirection when invitations are cleared.
 */
export function useSubscriptionViewManagement({
  hasPendingInvitations,
  isFetching,
}: UseSubscriptionViewManagementOptions): UseSubscriptionViewManagementResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const getDefaultView = useCallback((): SubscriptionView => {
    return hasPendingInvitations ? 'invitations' : 'subscription';
  }, [hasPendingInvitations]);

  const rawViewParam = searchParams.get('view');
  const viewParam: SubscriptionView | null =
    rawViewParam === 'subscription' || rawViewParam === 'invitations' ? rawViewParam : null;

  // If viewing invitations but none exist, use subscription view
  // Don't redirect while still fetching to avoid race conditions
  const view: SubscriptionView =
    viewParam === 'invitations' && !hasPendingInvitations && !isFetching
      ? 'subscription'
      : viewParam || getDefaultView();

  const setView = useCallback(
    (newView: SubscriptionView) => {
      setSearchParams({ view: newView });
    },
    [setSearchParams],
  );

  // Effect to update URL if we're on invitations view but no invitations exist
  // Don't redirect while still fetching to avoid race conditions
  useEffect(() => {
    if (viewParam === 'invitations' && !hasPendingInvitations && !isFetching) {
      setSearchParams({ view: 'subscription' }, { replace: true });
    }
  }, [viewParam, hasPendingInvitations, isFetching, setSearchParams]);

  return {
    view,
    setView,
  };
}
